/**
 * Central configuration for weather threats across the application
 *
 * This file contains all weather threat definitions, including their icons,
 * colors, descriptions, and utility functions. This ensures consistency
 * across all components that display weather threat information.
 */

import {
  Cloud,
  CloudSnow,
  CloudRain,
  Zap,
  Wind,
  Snowflake,
  Tornado,
  Waves,
  Thermometer,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

// Define the structure for threat configuration
export interface WeatherThreatConfig {
  /** Lucide icon component for the threat */
  icon: LucideIcon;
  /** Display name for the threat */
  displayName: string;
  /** Description of the threat */
  description: string;
  /** Primary color (CSS custom property from globals.css) */
  color: string;
  /** Background color for light mode displays */
  bgColor: string;
  /** Background color for dark mode displays */
  darkBgColor: string;
}

// Master list of all weather threat slugs
export const WEATHER_THREAT_SLUGS = [
  "widespread_winds",
  "extreme_cold",
  "tornadoes",
  "flooding",
  "snow",
  "thunderstorms",
  "damaging_thunderstorm_winds",
  "freezing_rain",
] as const;

// Type for valid threat slugs
export type WeatherThreatSlug = (typeof WEATHER_THREAT_SLUGS)[number];

// Central configuration for all weather threats using CSS custom properties from globals.css
export const WEATHER_THREATS_CONFIG: Record<
  WeatherThreatSlug,
  WeatherThreatConfig
> = {
  widespread_winds: {
    icon: Wind,
    displayName: "Widespread Winds",
    description: "Widespread wind threat",
    color: "hsl(var(--primary))", // Primary blue
    bgColor: "hsl(var(--primary) / 0.1)",
    darkBgColor: "hsl(var(--primary) / 0.2)",
  },
  extreme_cold: {
    icon: Thermometer,
    displayName: "Extreme Cold",
    description: "Extreme cold threat",
    color: "hsl(217, 91%, 60%)", // Blue
    bgColor: "hsl(217, 91%, 95%)",
    darkBgColor: "hsl(217, 91%, 30%)",
  },
  tornadoes: {
    icon: Tornado,
    displayName: "Tornadoes",
    description: "Tornado threat",
    color: "hsl(0, 84%, 60%)", // Red
    bgColor: "hsl(0, 84%, 95%)",
    darkBgColor: "hsl(0, 84%, 30%)",
  },
  flooding: {
    icon: Waves,
    displayName: "Flooding",
    description: "Flooding threat",
    color: "hsl(199, 89%, 48%)", // Cyan blue
    bgColor: "hsl(199, 89%, 95%)",
    darkBgColor: "hsl(199, 89%, 30%)",
  },
  snow: {
    icon: CloudSnow,
    displayName: "Snow",
    description: "Snow threat",
    color: "hsl(215, 16%, 47%)", // Gray blue
    bgColor: "hsl(215, 16%, 95%)",
    darkBgColor: "hsl(215, 16%, 30%)",
  },
  thunderstorms: {
    icon: Zap,
    displayName: "Thunderstorms",
    description: "Thunderstorm threat",
    color: "hsl(48, 96%, 53%)", // Yellow
    bgColor: "hsl(48, 96%, 95%)",
    darkBgColor: "hsl(48, 96%, 30%)",
  },
  damaging_thunderstorm_winds: {
    icon: Wind,
    displayName: "Damaging Thunderstorm Winds",
    description: "Damaging thunderstorm winds",
    color: "hsl(24, 95%, 53%)", // Orange
    bgColor: "hsl(24, 95%, 95%)",
    darkBgColor: "hsl(24, 95%, 30%)",
  },
  freezing_rain: {
    icon: CloudRain,
    displayName: "Freezing Rain",
    description: "Freezing rain threat",
    color: "hsl(200, 18%, 46%)", // Steel blue
    bgColor: "hsl(200, 18%, 95%)",
    darkBgColor: "hsl(200, 18%, 30%)",
  },
} as const;

// Utility function to get threat configuration by slug
export function getThreatConfig(slug: string): WeatherThreatConfig | undefined {
  return WEATHER_THREATS_CONFIG[slug as WeatherThreatSlug];
}

// Utility function to get threat icon by slug
export function getThreatIcon(slug: string): LucideIcon | undefined {
  return getThreatConfig(slug)?.icon;
}

// Utility function to get threat color by slug (for badges, etc.)
export function getThreatColor(slug: string): string | undefined {
  return getThreatConfig(slug)?.color;
}

// Utility function to get chart color by slug (for charts) - now uses the same color as main color
export function getThreatChartColor(slug: string): string | undefined {
  return getThreatColor(slug);
}

// Utility function to get threat background color by slug
export function getThreatBgColor(
  slug: string,
  isDark = false,
): string | undefined {
  const config = getThreatConfig(slug);
  return isDark ? config?.darkBgColor : config?.bgColor;
}

// Utility function to get threat display name by slug
export function getThreatDisplayName(slug: string): string | undefined {
  return getThreatConfig(slug)?.displayName;
}

// Utility function to get threat description by slug
export function getThreatDescription(slug: string): string | undefined {
  return getThreatConfig(slug)?.description;
}

// Utility function to check if a slug is a valid threat slug
export function isThreatSlug(slug: string): slug is WeatherThreatSlug {
  return WEATHER_THREAT_SLUGS.includes(slug as WeatherThreatSlug);
}

// Utility function to filter array to only valid threat slugs
export function filterThreatSlugs(slugs: string[]): WeatherThreatSlug[] {
  return slugs.filter(isThreatSlug);
}

// Interface for threat indicator data (compatible with existing ThreatIndicator interface)
export interface ThreatIndicator {
  id: string;
  slug: string;
  name: string;
  value: string | number;
  isActive: boolean;
  color?: string;
  description?: string;
  nodeId?: string;
}

// Helper function to process forecast data into threat indicators
export function processThreatData(forecastData: any): ThreatIndicator[] {
  // Handle different data structures
  let variableValues: any[] = [];

  if (Array.isArray(forecastData)) {
    // If it's already an array of variable_values
    variableValues = forecastData;
  } else if (forecastData?.variable_values) {
    // If it's an object with variable_values property (new format)
    variableValues = forecastData.variable_values;
  } else if (forecastData) {
    // If it's a single forecast object, extract variable_values
    variableValues = forecastData.variable_values || [];
  }

  if (!variableValues || variableValues.length === 0) {
    return [];
  }

  return variableValues
    .filter((vv) => isThreatSlug(vv.variable_slug))
    .map((vv) => ({
      id: vv.variable_id || vv.id,
      slug: vv.variable_slug,
      name: vv.variable_name,
      value: vv.value,
      isActive: vv.value === "1" || vv.value === 1,
      description: vv.variable_description,
    }));
}

// Legacy compatibility exports (for backward compatibility)
export const THREAT_SLUGS = WEATHER_THREAT_SLUGS; // Deprecated: use WEATHER_THREAT_SLUGS
